// components/GeneratingLayers.tsx
import React, { useRef, useEffect, useState } from 'react';
import anime from 'animejs';
import { GenerationTask } from '#types/types';
import { useKernelStore } from '@/kernel/kernelStores';
const EMPTY_GENERATION_TASKS: GenerationTask[] = [];

interface GeneratingLayerItemProps {
  task: GenerationTask;
  projectId: string;
}

const GeneratingLayerItem = React.memo<GeneratingLayerItemProps>(({ task, projectId }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const pulseRef = useRef<HTMLDivElement>(null);
  const [maskUrl, setMaskUrl] = useState<string | null>(null);
  const zoomLevel = useKernelStore((state) => {
    const runtime = state.inventory.Project.registry[projectId]?.runtime;
    const value = runtime?.zoomLevel;
    return typeof value === 'number' && Number.isFinite(value) ? value : 1;
  });
  const canRender =
    !!task.rect && !!task.targetWidth && !!task.targetHeight && !!task.upscaleFactor && !task.layer;

  // Handle mask file to blob URL conversion
  useEffect(() => {
    if (!task.maskFile) {
      setMaskUrl(null);
      return;
    }

    const url = URL.createObjectURL(task.maskFile);
    setMaskUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [task.maskFile]);

  // Pulse animation using anime.js
  useEffect(() => {
    if (!pulseRef.current) return;

    const animation = anime({
      targets: pulseRef.current,
      backgroundColor: [
        'rgba(70, 70, 70, 0)',
        'rgba(70, 70, 70, 0.35)'
      ],
      duration: 620, // Matches the 0.62s from the original shader/CSS
      direction: 'alternate',
      loop: true,
      easing: 'easeInOutSine'
    });

    return () => {
      animation.pause();
    };
  }, []);

  useEffect(() => {
    const applyBorderRadius = (zoomLevel: number) => {
      if (!containerRef.current) return;
      const safeZoom = Math.max(zoomLevel, 0.0001);
      containerRef.current.style.borderRadius = `${25.0 / safeZoom}px`;
    };

    applyBorderRadius(zoomLevel);
  }, [zoomLevel]);

  if (!canRender || !task.rect || !task.targetWidth || !task.targetHeight || !task.upscaleFactor) {
    return null;
  }

  const width = task.targetWidth / task.upscaleFactor;
  const height = task.targetHeight / task.upscaleFactor;
  const isWaitingForMaskUrl = !!task.maskFile && !maskUrl;

  return (
    <div
      ref={containerRef}
      className="absolute overflow-hidden pointer-events-none"
      style={{
        top: task.rect.y,
        left: task.rect.x,
        width: width,
        height: height,
        borderRadius: '25px',
        zIndex: 10,
        // Never flash the full generation rectangle while the task-local mask File is being
        // converted to a blob URL. The pulse becomes visible only once clipping is active.
        visibility: isWaitingForMaskUrl ? 'hidden' : 'visible',
        // CSS Masking equivalent to the shader's texture sampling
        WebkitMaskImage: maskUrl ? `url(${maskUrl})` : 'none',
        maskImage: maskUrl ? `url(${maskUrl})` : 'none',
        WebkitMaskSize: '100% 100%',
        maskSize: '100% 100%',
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
      }}
    >
      <div 
        ref={pulseRef}
        className="w-full h-full"
      />
    </div>
  );
});

GeneratingLayerItem.displayName = 'GeneratingLayerItem';

interface GeneratingLayersProps {
  projectId: string;
}

const GeneratingLayers = React.memo<GeneratingLayersProps>(({ projectId }) => {
  const generationTasks = useKernelStore((state) => {
    if (!projectId) return EMPTY_GENERATION_TASKS;
    const runtime = state.inventory.Project.registry[projectId]?.runtime;
    return Array.isArray(runtime?.generationTasks) ? runtime.generationTasks : EMPTY_GENERATION_TASKS;
  });

  return (
    <>
      {generationTasks.map((task) => {
        if (task.rect && task.targetWidth && task.targetHeight && task.upscaleFactor && !task.layer && !task.error) {
          return <GeneratingLayerItem key={task.id} task={task} projectId={projectId} />;
        }
        return null;
      })}
    </>
  );
});

GeneratingLayers.displayName = 'GeneratingLayers';

export default GeneratingLayers;

